/**
 * External Data Retrieval Action Definitions
 *
 * Actions for looking up data from external databases (RCSB PDB,
 * UniProt, ChEMBL, KLIFS, PubChem).
 *
 * @module gibbs/registry/actions/external-data
 */

import { WorkflowTypes as T } from '../types';
import { param, pre } from '../schema';

// ─── lookup-pdb-structure ───────────────────────────────────────────────────

export const lookupPdbStructure = {
    id: 'lookup-pdb-structure',
    name: 'Fetch PDB Structure from RCSB',
    category: 'External Data Retrieval',
    description: 'Download a protein structure from the RCSB Protein Data Bank. Also retrieves chemical component SMILES for ligands.',
    inputs: [
        param('pdbId', 'string', '4-character PDB code'),
    ],
    outputs: [
        param('structure', T.PDB, 'Protein structure data (PDB or CIF format)'),
        param('ccdSmiles', T.SMILES, 'Ligand chemical component SMILES', false),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('internet_available', 'Internet access is required'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: null,
    serverCommand: 'getCCDSmiles',
    jobType: null,
};

// ─── lookup-uniprot ─────────────────────────────────────────────────────────

export const lookupUniprot = {
    id: 'lookup-uniprot',
    name: 'UniProt ID Mapping',
    category: 'External Data Retrieval',
    description: 'Map PDB identifiers to UniProt accession numbers. Uses UniProt ID mapping service with polling for async results.',
    inputs: [
        param('pdbIds', 'object', 'PDB identifiers to map (string array)'),
    ],
    outputs: [
        param('mapping', 'object', '{pdbId: uniprotAccession} mapping'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('internet_available', 'Internet access is required'),
        pre('protein_loaded', 'A protein with a PDB ID must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: null,
    serverCommand: 'uniprotLookup',
    jobType: null,
};

// ─── lookup-chembl ──────────────────────────────────────────────────────────

export const lookupChembl = {
    id: 'lookup-chembl',
    name: 'ChEMBL Compound/Target Lookup',
    category: 'External Data Retrieval',
    description: 'Look up compounds and targets in ChEMBL database. Supports lookup by InChI key, SMILES (flexmatch), or UniProt ID. Returns compound metadata, target information, and activity data.',
    inputs: [
        param('compound', T.Compound, 'Compound for InChI key / SMILES lookup', false),
        param('uniprotId', 'string', 'UniProt ID for target lookup', false),
    ],
    outputs: [
        param('chemblId', 'string', 'ChEMBL identifier'),
        param('targetInfo', 'object', 'Target metadata', false),
        param('activityData', 'object', 'Bioactivity assay results', false),
    ],
    parameters: [
        param('allowTautomers', 'boolean', 'Include tautomeric forms', false, { defaultValue: false }),
    ],
    dependsOn: [],
    preconditions: [
        pre('internet_available', 'Internet access is required'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: null,
    serverCommand: 'chemblLookup',
    jobType: null,
};

// ─── lookup-klifs ───────────────────────────────────────────────────────────

export const lookupKlifs = {
    id: 'lookup-klifs',
    name: 'KLIFS Kinase Data Lookup',
    category: 'External Data Retrieval',
    description: 'Look up kinase-ligand interaction data from the KLIFS (Kinase-Ligand Interaction Fingerprints and Structures) database. Returns kinase pocket data, structure IDs, and interaction metadata.',
    inputs: [
        param('pdbIds', 'object', 'PDB codes to look up (string array)'),
    ],
    outputs: [
        param('klifsData', 'object', 'Kinase metadata per PDB (pocket, structure ID, etc.)'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('internet_available', 'Internet access is required'),
        pre('protein_loaded', 'A protein with a PDB ID must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: null,
    serverCommand: 'klifsLookupByPdbIds',
    jobType: null,
};

// ─── lookup-pubchem ─────────────────────────────────────────────────────────

export const lookupPubchem = {
    id: 'lookup-pubchem',
    name: 'PubChem Compound Lookup',
    category: 'External Data Retrieval',
    description: 'Look up compounds in PubChem by SDF, SMILES, or name. Returns PubChem CID and IUPAC name.',
    inputs: [
        param('fields', 'object', '{sdf, smiles, name} — at least one lookup field'),
    ],
    outputs: [
        param('cid', 'string', 'PubChem Compound ID'),
        param('iupacName', 'string', 'IUPAC systematic name'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('internet_available', 'Internet access is required'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: null,
    serverCommand: 'pubChemLookup',
    jobType: null,
};

// ─── Default Export ─────────────────────────────────────────────────────────

export default [
    lookupPdbStructure,
    lookupUniprot,
    lookupChembl,
    lookupKlifs,
    lookupPubchem,
];
